import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import userService from '../../services/userService';
import { useAuth } from '../../context/AuthContext';

// Schéma de validation pour le profil
const ProfileSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, 'Trop court')
    .max(50, 'Trop long')
    .required('Obligatoire'),
  email: Yup.string()
    .email('Email invalide')
    .required('Obligatoire')
});

// Schéma de validation pour le changement de mot de passe
const PasswordSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .required('Le mot de passe actuel est obligatoire'),
  newPassword: Yup.string()
    .min(6, 'Le mot de passe doit contenir au moins 6 caractères')
    .required('Le nouveau mot de passe est obligatoire'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Les mots de passe doivent correspondre')
    .required('La confirmation du mot de passe est obligatoire')
});

const ProfileForm = () => {
  const { currentUser, updateCurrentUser } = useAuth();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  const initialValues = {
    username: currentUser?.username || '',
    email: currentUser?.email || ''
  };
  
  const passwordInitialValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
  
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setErrorMessage('');
      setSuccessMessage('');
      
      const response = await userService.updateUser(currentUser._id, values);
      
      if (response.success) {
        updateCurrentUser(values);
        setSuccessMessage('Profil mis à jour avec succès');
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Erreur lors de la mise à jour du profil');
      console.error('Erreur lors de la mise à jour du profil:', err);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handlePasswordSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setErrorMessage('');
      setSuccessMessage('');
      
      const response = await userService.updatePassword(currentUser._id, values);
      
      if (response.success) {
        setSuccessMessage('Mot de passe mis à jour avec succès');
        resetForm();
        setShowPasswordForm(false);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Erreur lors de la mise à jour du mot de passe');
      console.error('Erreur lors de la mise à jour du mot de passe:', err);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="bg-white shadow rounded-lg p-6 mb-8">
      <h2 className="text-2xl font-bold mb-6">Mon profil</h2>
      
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-400 text-green-700 rounded">
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border border-red-400 text-red-700 rounded">
          {errorMessage}
        </div>
      )}
      
      <Formik
        initialValues={initialValues}
        validationSchema={ProfileSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  Nom d'utilisateur
                </label>
                <Field
                  type="text"
                  name="username"
                  id="username"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <ErrorMessage name="username" component="div" className="text-red-500 text-xs mt-1" />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <Field
                  type="email"
                  name="email"
                  id="email"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-xs mt-1" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Rôle
                </label>
                <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
                  {currentUser?.role === 'admin' 
                    ? 'Administrateur' 
                    : currentUser?.role === 'admin_assistant' 
                      ? 'Assistant administrateur' 
                      : 'Visiteur'}
                </div>
              </div>
              
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  {isSubmitting ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="ml-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  {showPasswordForm ? 'Annuler' : 'Changer de mot de passe'}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      
      {showPasswordForm && (
        <div className="mt-8 pt-6 border-t">
          <h3 className="text-xl font-bold mb-4">Changer mon mot de passe</h3>
          
          <Formik
            initialValues={passwordInitialValues}
            validationSchema={PasswordSchema}
            onSubmit={handlePasswordSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                      Mot de passe actuel
                    </label>
                    <Field
                      type="password"
                      name="currentPassword"
                      id="currentPassword"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <ErrorMessage name="currentPassword" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                  
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                      Nouveau mot de passe
                    </label>
                    <Field
                      type="password"
                      name="newPassword"
                      id="newPassword"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <ErrorMessage name="newPassword" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                  
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirmer le nouveau mot de passe
                    </label>
                    <Field
                      type="password"
                      name="confirmPassword"
                      id="confirmPassword"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                  
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                    >
                      {isSubmitting ? 'Modification...' : 'Modifier le mot de passe'}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default ProfileForm;